import type { Option } from "@/Components/UI/TcSelect.vue";
import OT from "@opentok/client";

export enum VIDEO_CALL_STATUS {
  PERMISSIONS = "PERMISSIONS",
  WAITING = "WAITING",
  CALLING = "CALLING",
  CLOSING = "CLOSING",
  UNPUBLISHING = "UNPUBLISHING",
}

export const VIDEO_CALL_STATUS_CALLING: (VIDEO_CALL_STATUS | undefined)[] = [
  VIDEO_CALL_STATUS.CALLING,
  VIDEO_CALL_STATUS.UNPUBLISHING,
];

export enum CAM_MODE {
  USER = "user",
  ENVIRONMENT = "environment",
}

export enum TRACK_KIND {
  VIDEO = "video",
  AUDIO = "audio",
}

export enum PARTICIPANT {
  LOCAL = "LOCAL",
  REMOTE = "REMOTE",
  SHARE_SCREEN = "SHARE_SCREEN",
}

export enum PARTICIPANT_TYPE {
  LOCAL = "local",
  REMOTE = "remote",
  SHARE_SCREEN = "screen",
}

export enum VIDEO_CALL_EXCEPTIONS {
  NOT_PERMISSIONS = "NOT_PERMISSIONS",
  INVALID_CONFIG = "INVALID_CONFIG",
  INVALID_PARTICIPANT_CONFIG = "INVALID_PARTICIPANT_CONFIG",
  INIT_PUBLISHER = "INIT_PUBLISHER",
  CLOSE_CALL_BEFORE_INIT = "CLOSE_CALL_BEFORE_INIT",
  CONNECT_SESSION = "CONNECT_SESSION",
  CONNECT_SESSION_RETRY = "CONNECT_SESSION_RETRY",
  PUBLISH_STREAMS = "PUBLISH_STREAMS",
  INVALID_SUBSCRIBE_STREAM = "INVALID_SUBSCRIBE_STREAM",
  ERROR_SUBSCRIBE_STREAM = "ERROR_SUBSCRIBE_STREAM",
  ERROR_PUBLISH_SCREEN = "ERROR_PUBLISH_SCREEN",
}

export type trackStatus = Record<TRACK_KIND, boolean | undefined>;
export type streamsStatus = Record<
  PARTICIPANT,
  trackStatus & { isUserVideoAction: boolean; isUserAudioAction: boolean }
>;

export type resolution =
  | "1920x1080"
  | "1280x960"
  | "1280x720"
  | "640x480"
  | "640x360"
  | "320x240"
  | "320x180";

export interface VideoDimensions {
  width: number;
  height: number;
}

export enum VideoCallErrorType {
  CONNECT_TO_ROOM = "connect_to_room_error",
  PERMISSION_DENIED = "permission_denied_error",
  START_SHARE_SCREEN = "start_share_screen_error",
  ACCEPTING_CALL = "accepting_call_error",
  MISSED_CALL = "missed_call_error",
  MISSED_CALL_SAFARI = "missed_call_safari_error",
  CAMERA_SWAP = "camera_swap_error",
  TWILIO_CONNECTION_ERROR = "twilio_connection_error",
  SHORT_CALL = "short_call",
}

export enum VideoCallStatus {
  OK = "OK",
  KO = "KO",
  ERROR = "ERROR",
}

export enum VIDEO_CALL_NETWORK_QUALITY {
  HIGHEST = "HIGHEST",
  HIGH = "HIGH",
  MEDIUM = "MEDIUM",
  LOW = "LOW",
  LOWEST = "LOWEST",
}

export interface VonageError extends OT.OTError {
  code?: number;
}

export enum VONAGE_EXCEPTION_NAMES {
  OT_INVALID_HTTP_STATUS = "OT_INVALID_HTTP_STATUS",
}

export enum VONAGE_EXCEPTION_CODES {
  RETRY_CONNECT = 2001,
}

export interface VideoCallDeviceConfig {
  type: CAM_MODE;
  deviceId: string | undefined;
}

export interface DeviceInfo {
  id: string;
  label: string;
}
export interface DevicesInfo {
  audio?: DeviceInfo;
  video?: DeviceInfo;
}

export interface VideoCallConfig {
  videoId?: string; // undefined for start videocall with no video
  audioId: string | false;
  outputId?: string; // undefined for start videocall with default output audio
  blur: "low" | "high" | false;
  mirror?: boolean;
  video: boolean;
  audio: boolean;
  updatedAt?: string;
}

export type deviceKind = "videoInput" | "audioInput" | "audioOutput";

export interface Device extends Omit<OT.Device, "kind"> {
  kind: deviceKind;
}

export interface CallConfigType {
  videoId?: Option<string>;
  audioId?: Option<string>;
  outputId?: Option<string>;
  blur?: boolean;
  mirror?: boolean;
  video: boolean;
  audio: boolean;
}

export type ConfigCallFormType = Pick<CallConfigType, "audioId" | "videoId" | "outputId" | "blur" | "mirror">;
